import axios, { AxiosInstance } from 'axios';
import Vue from 'vue';
import moment from 'moment';
import 'moment-duration-format';
import * as i18next from 'i18next';
import * as api from './api';
import * as auth from './auth';
import * as utils from './utils';
import { Filters } from './filters';
import { Directives } from './directives';

export interface AppConfig {
    apiUrl: string;
    cdnUrl: string;
    clientDevice: api.ClientDevice;
    cookieDomain: string;
    currencyCode: string;
    currencySymbol: string;
    markets: any;
    marketCode: string;
    mobileUrl: string;
    mobileRedirectAllowed: boolean;
    desktopUrl: string;
    environment: string;
    host: string;
    instance: string;
    locale: string;
    localeDictionary: any;
    staticPathsWithHash: any;
    theme: string;
    themeMarker: number;
    support: {
        phone: string;
        telegram: string;
    };
}

type ErrorLevel = 'WARNING' | 'ERROR' | 'INFO';

// appConfig is defined in /Views/Shared/_Scripts.cshtml
declare let appConfig: AppConfig;

export class G2S {
    static get config(): AppConfig {
        return appConfig;
    }

    static get isDev(): boolean {
        return G2S.config.environment === 'Dev';
    }

    static get isSecure(): boolean {
        return G2S.config.desktopUrl.indexOf('https') === 0;
    }

    static get nbsp(): string {
        return '\xA0';
    }

    static $http: AxiosInstance;

    static allowLogError(message: string) {
        const disallowMsg = [
            '__ybro.readability.runDetection'
        ];
        return !disallowMsg.some((msg) => message.includes(msg));
    }

    static init() {
        console.log('G2S.init: environment=' + G2S.config.environment
            + ', secure=' + G2S.isSecure
            + ', locale=' + G2S.config.locale);

        if (G2S.config.environment === 'Production') {
            utils._LTracker.push({
                'logglyKey': '4407a3a2-9237-41a4-a613-7d4ba91da62a'
            });

            const _onerror = window.onerror;

            window.onerror = function(msg: string, url, line, col, err) {
                if (
                    G2S.allowLogError(msg)
                    && msg != 'ResizeObserver loop limit exceeded'
                    && msg != 'Script error.' // It's JS error in external script which can't not be access by out code
                ) {
                    utils._LTracker.push({
                        category: 'WindowJsError',
                        level: 'ERROR',
                        message: msg,
                        line: line,
                        col: col,
                        stack: err ? err.stack : 'n/a',
                        url: window.location.href,
                        userAgent: navigator.userAgent
                    });
                }

                if (_onerror && typeof _onerror === 'function') {
                    // eslint-disable-next-line prefer-rest-params
                    _onerror.apply(window, arguments);
                }
            };
        }

        // Init global axios client
        G2S.$http = axios.create();
        G2S.$http.defaults.baseURL = G2S.config.apiUrl;
        G2S.$http.interceptors.request.use((config) => {
            const d1 = 'pp', d2 = 'ecr';
            const d1ar = ['C', '5', 'E', '2', '3', 'A', '9', 'A', '5', '8', 'F', '2', 'C', '1', 'E', '9', 'C', 'A', '7', 'B', '0', 'F', '5', '9', '4', '8', '1', '3', '7', '1', '9', 'C', '0', '4', '5', '8', 'E', 'F', '0', '8'];
            const d2ar = ['A', 'D', '7', '3', '6', '8', '1', 'B', 'C', '7', '4', '1', 'F', 'D', 'E', 'D', 'F', '3', '8', '8', 'E', 'F', '7', '3', '3', '2', '5', '4', '6', 'D', 'C', 'C', '1', '7', '0', '0', '1', 'C', '5', '5', '0', '0', 'C', 'C', '2', 'E', '9', '9', '6', '0', '8', 'B', '7', '6', 'B', 'A', '9', 'B', 'D', '6', '2', '6', 'D', '2'];
            // Don't remove ершы condition
            if (config.data instanceof FormData) {
                config.data.append('locale', G2S.config.locale);
                config.data.append('marketCode', G2S.config.marketCode);
                config.data.append('theme', G2S.config.theme);
                config.data.append('host', G2S.config.host);
                config.data.append('instance', G2S.config.instance);
                config.data.append('a' + d1 + 'Id', d1ar.join(''));
                config.data.append('a' + d1 + 'S' + d2 + 'et', d2ar.join(''));
            } else {
                // Check if it's IApiRequest
                if (config.data) {
                    config.data['a' + d1 + 'Id'] = d1ar.join('');
                    config.data['a' + d1 + 'S' + d2 + 'et'] = d2ar.join('');
                }
                utils.Cookies.getSplitTest();
                
                config.data = {
                    ...config.data,
                    currencyCode: G2S.config.currencyCode,
                    locale: G2S.config.locale,
                    theme: G2S.config.theme,
                    host: G2S.config.host,
                    instance: G2S.config.instance,
                    marketCode: G2S.config.marketCode
                };
            }
            return config;
        });

        // Init i18next
        const dictionary = {};

        dictionary[G2S.config.locale] = {
            translation: G2S.config.localeDictionary
        };

        i18next.init({
            lng: G2S.config.locale,
            resources: dictionary
        });

        // Init moment.js
        moment.locale(G2S.config.locale); // Moment locale has been packed by Webpack

        // Fix moment localization
        switch(G2S.config.marketCode) {
            case 'en':
                moment.updateLocale(G2S.config.marketCode, {
                    longDateFormat : {
                        LT: 'HH:mm',
                        LTS: 'HH:mm:ss',
                        L: 'DD.MM.YYYY',
                        LL: 'D MMMM YYYY',
                        LLL: 'D MMMM YYYY HH:mm',
                        LLLL: 'dddd D MMMM YYYY HH:mm'
                    }
                });
                i18next.default.addResource(G2S.config.locale, 'translation', 'label_date_format', 'DD.MM.YYYY');
                break;

            case 'ru':
                moment.updateLocale(G2S.config.marketCode, {
                    longDateFormat: {
                        LT: 'HH:mm',
                        LTS: 'LT:ss',
                        L: 'DD.MM.YYYY',
                        LL: 'D MMMM YYYY',
                        LLL: 'D MMMM YYYY, LT',
                        LLLL: 'dddd, D MMMM YYYY, LT'
                    },

                    monthsShort: {
                        format: 'янв_фев_мар_апр_мая_июн_июл_авг_сен_окт_ноя_дек'.split('_'),
                        standalone: 'янв_фев_мар_апр_май_июн_июл_авг_сен_окт_ноя_дек'.split('_')
                    }
                });
                break;

            case 'ae':
            case 'il':
                moment.updateLocale(G2S.config.marketCode, {
                    longDateFormat : {
                        LT: 'HH:mm',
                        LTS: 'HH:mm:ss',
                        L: 'DD/MM/YYYY',
                        LL: 'D MMMM YYYY',
                        LLL: 'D MMMM YYYY HH:mm',
                        LLLL: 'dddd D MMMM YYYY HH:mm'
                    }
                });
                i18next.default.addResource(G2S.config.locale, 'translation', 'label_date_format', 'DD/MM/YYYY');
                break;

            case 'dk':
            case 'in':
            case 'my':
                moment.updateLocale(G2S.config.marketCode, {
                    longDateFormat : {
                        LT: 'HH:mm',
                        LTS: 'HH:mm:ss',
                        L: 'DD-MM-YYYY',
                        LL: 'D MMMM YYYY',
                        LLL: 'D MMMM YYYY HH:mm',
                        LLLL: 'dddd D MMMM YYYY HH:mm'
                    }
                });
                i18next.default.addResource(G2S.config.locale, 'translation', 'label_date_format', 'DD-MM-YYYY');
                break;
        }

        if ('my_ar'.split('_').includes(G2S.config.locale)) {
            (<any>moment())._locale.postformat = function (string) { return string; };
        }

        // Init Vue.js
        (<any>Vue).prototype.$i18 = i18next;
        (<any>Vue).prototype.$g2s = G2S.config;
        (<any>Vue).prototype.$m = moment;
        (<any>Vue).prototype.$window = window;
        (<any>Vue).prototype.$auth = new auth.AuthService();
        (<any>Vue).prototype.$ga = new utils.GoogleAnalyticsService();
        Vue.filter('baggage', Filters.baggage);
        Vue.filter('customBaggage', Filters.customBaggage);
        Vue.filter('number', Filters.number);
        Vue.filter('phone', Filters.phone);
        Vue.filter('price', Filters.price);
        Vue.filter('priceWithCurrency', Filters.priceWithCurrency);
        Vue.directive('focus', Directives.focus);

        // Init AviasalesInject service to receive passengers from Aviasales mobile app
        (<any>window).AviasalesInject = new utils.AviasalesInjectService();

        if (G2S.config.environment === 'Production') {
            Vue.config.errorHandler = function (error, vm: any) {
                let componentName = '';

                if (vm.$root === vm) {
                    componentName = 'root instance';
                } else {
                    const name = vm._isVue ? vm.$options.name || vm.$options._componentTag : vm.name;

                    componentName = name ? 'component <' + name + '>' : 'anonymous component';
                    componentName += vm._isVue && vm.$options.__file ? ' at ' + vm.$options.__file : '';
                }
                utils._LTracker.push({
                    category: 'VueJsError',
                    level: 'ERROR',
                    componentName: componentName,
                    message: error.message,
                    stack: error.stack ? error.stack : 'n/a',
                    url: window.location.href,
                    userAgent: navigator.userAgent
                });

                // Output error anyway
                console.error(error);
            };
        }
    }
    
    static logError(message: string, level?: ErrorLevel) {
        if (G2S.config.environment === 'Production' && G2S.allowLogError(message)) {
            utils._LTracker.push({ 
                category: 'CustomJsError',
                level: level ?? 'ERROR',
                message: message,
                url: window.location.href,
                userAgent: navigator.userAgent
            });
        }

        console.error(message);
    }
}
